import { useState } from 'react'

import { ProductBrand } from '@/app/ProductBrand'
import { ENGLISH_DOMAIN_CODE, englishLevel } from '@/domains/english/catalog/levels'
import { type MaterialConsentApi } from '@/platform/family/consent'
import { type Learner } from '@/platform/learners/api'
import { STUDY_DAYS } from '@/platform/learners/profile'

import { useFocusOnMount } from './learning/focus'
import { type DemoRunReport } from './materials/model'
import { type LoadInsightPreview } from './parent/insight'
import { LearningOverview } from './parent/LearningOverview'
import { MaterialConsentCard } from './parent/MaterialConsentCard'
import { type LoadMaterialActivity } from './parent/MaterialActivityView'

/**
 * Parent Home (FR-02 / FR-03, Slice 1 product shell): the parent's area of
 * the app, with two sections. "Gia đình" is the family overview with the
 * existing learner profile management and the entry to child mode; "Học tập"
 * is the learning overview and Learning Insight preview (D-14.4), which is
 * honest about having no real data yet and shows DEMO data only where allowed.
 * Navigation sits at the bottom on phones and in the header from tablet up.
 */

export interface ParentHomeProps {
  readonly familyName: string
  readonly learners: readonly Learner[]
  readonly demoInsight: LoadInsightPreview | null
  /** Show the DEMO school-material preview (local/development only). */
  readonly demoMaterialsEnabled?: boolean
  /** Last DEMO school-material run on this device, memory only. */
  readonly demoRun?: DemoRunReport | null
  readonly onAdd: () => void
  readonly onEdit: (learner: Learner) => void
  readonly onEnterChildMode: (learner: Learner) => void
  readonly onSignOut: () => void
  /** Learners that have their own PIN (D-16). */
  readonly pinConfigured?: ReadonlySet<string>
  /** Set or reset a learner's PIN (parent PIN step-up on the next screen). */
  readonly onSetLearnerPin?: (learner: Learner) => void
  /** Hand the device to the children: the learner-selection screen. */
  readonly onLearnerEntry?: () => void
  /** Real persisted learning evidence (D-17). */
  readonly loadMaterialActivity?: LoadMaterialActivity | null
  /** Parent consent to school-material processing (NFR-05); null where reading is off. */
  readonly materialConsent?: MaterialConsentApi | null
}

const NO_PINS: ReadonlySet<string> = new Set()

type Tab = 'family' | 'learning'

const TABS: readonly { id: Tab; label: string; icon: string }[] = [
  { id: 'family', label: 'Gia đình', icon: '🏠' },
  { id: 'learning', label: 'Học tập', icon: '📊' },
]

function levelLine(learner: Learner): string {
  const english = learner.enrollments.find((e) => e.domain_code === ENGLISH_DOMAIN_CODE)
  if (english === undefined) return 'Chưa chọn cấp độ'
  const current = englishLevel(english.current_level)?.name ?? english.current_level
  const target = englishLevel(english.target_level)?.name ?? english.target_level
  return current === target ? current : `${current} → ${target}`
}

function scheduleLine(learner: Learner): string {
  const days = STUDY_DAYS.filter((d) => learner.study_days.includes(d.day))
    .map((d) => d.short)
    .join(', ')
  return `${String(learner.daily_minutes)} phút · ${days}`
}

export function ParentHome({
  familyName,
  learners,
  demoInsight,
  demoMaterialsEnabled = false,
  demoRun = null,
  onAdd,
  onEdit,
  onEnterChildMode,
  onSignOut,
  pinConfigured = NO_PINS,
  onSetLearnerPin,
  onLearnerEntry,
  loadMaterialActivity = null,
  materialConsent = null,
}: ParentHomeProps): React.JSX.Element {
  const [tab, setTab] = useState<Tab>('family')
  // Focus follows a tab change, but not the first render of Parent Home.
  const [changedTab, setChangedTab] = useState(false)

  const openTab = (next: Tab) => {
    setChangedTab(true)
    setTab(next)
  }

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="border-b border-slate-200 dark:border-slate-800">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-4 px-5 py-3">
          <ProductBrand tagline />
          <nav
            aria-label="Khu vực phụ huynh"
            className="fixed inset-x-0 bottom-0 z-10 flex border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)] md:static md:border-0 md:bg-transparent md:pb-0 dark:border-slate-800 dark:bg-slate-950 md:dark:bg-transparent"
          >
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                aria-current={tab === t.id ? 'page' : undefined}
                className="flex h-16 flex-1 flex-col items-center justify-center gap-0.5 text-base text-slate-600 aria-[current=page]:font-semibold aria-[current=page]:text-sky-800 md:h-11 md:flex-none md:flex-row md:gap-2 md:rounded-xl md:px-4 md:aria-[current=page]:bg-sky-50 dark:text-slate-400 dark:aria-[current=page]:text-sky-300 md:dark:aria-[current=page]:bg-sky-950"
                onClick={() => {
                  openTab(t.id)
                }}
              >
                <span aria-hidden>{t.icon}</span>
                {t.label}
              </button>
            ))}
          </nav>
          <div className="flex flex-col items-end text-right">
            <p className="text-sm text-slate-600 dark:text-slate-400">{familyName}</p>
            <button
              type="button"
              className="px-1 text-slate-600 underline dark:text-slate-400"
              onClick={onSignOut}
            >
              Đăng xuất
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-5 pt-6 pb-28 md:pb-10">
        {tab === 'family' ? (
          <FamilyOverview
            key="family"
            focusHeading={changedTab}
            learners={learners}
            onAdd={onAdd}
            onEdit={onEdit}
            onEnterChildMode={onEnterChildMode}
            pinConfigured={pinConfigured}
            materialConsent={materialConsent}
            {...(onSetLearnerPin === undefined ? {} : { onSetLearnerPin })}
            {...(onLearnerEntry === undefined ? {} : { onLearnerEntry })}
            onOpenLearning={() => {
              openTab('learning')
            }}
          />
        ) : (
          <LearningOverview
            key="learning"
            learners={learners}
            demoInsight={demoInsight}
            demoMaterialsEnabled={demoMaterialsEnabled}
            demoRun={demoRun}
            loadMaterialActivity={loadMaterialActivity}
          />
        )}
      </main>
    </div>
  )
}

function FamilyOverview({
  focusHeading,
  learners,
  onAdd,
  onEdit,
  onEnterChildMode,
  pinConfigured,
  materialConsent,
  onSetLearnerPin,
  onLearnerEntry,
  onOpenLearning,
}: {
  focusHeading: boolean
  learners: readonly Learner[]
  onAdd: () => void
  onEdit: (learner: Learner) => void
  onEnterChildMode: (learner: Learner) => void
  pinConfigured: ReadonlySet<string>
  materialConsent: MaterialConsentApi | null
  onSetLearnerPin?: (learner: Learner) => void
  onLearnerEntry?: () => void
  onOpenLearning: () => void
}): React.JSX.Element {
  const heading = useFocusOnMount<HTMLHeadingElement>(focusHeading)
  const missingPin = learners.filter((l) => !pinConfigured.has(l.id))
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 ref={heading} tabIndex={-1} className="text-2xl font-semibold">
          Hồ sơ của các bé
        </h1>
        {onLearnerEntry !== undefined && (
          <button
            type="button"
            className="h-11 rounded-xl bg-emerald-700 px-4 font-semibold text-white"
            onClick={onLearnerEntry}
          >
            Cho con vào học
          </button>
        )}
      </div>

      {onSetLearnerPin !== undefined && missingPin.length > 0 && (
        <p
          role="note"
          className="rounded-xl bg-amber-50 px-3 py-2 text-amber-950 dark:bg-amber-950/40 dark:text-amber-100"
        >
          {missingPin.map((l) => l.nickname).join(', ')} chưa có mã PIN riêng. Bé cần mã
          PIN để tự vào học. Bấm “Đặt mã PIN” trên hồ sơ của bé.
        </p>
      )}

      <ul className="grid gap-3 md:grid-cols-2">
        {learners.map((learner) => (
          <li
            key={learner.id}
            className="rounded-2xl border border-slate-200 p-4 dark:border-slate-800"
          >
            <div className="flex items-center gap-3">
              <span
                className="flex size-12 items-center justify-center rounded-full bg-amber-100 text-2xl dark:bg-amber-900/40"
                aria-hidden
              >
                🧒
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-lg font-semibold">{learner.nickname}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {levelLine(learner)}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {scheduleLine(learner)}
                </p>
                {onSetLearnerPin !== undefined && (
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {pinConfigured.has(learner.id)
                      ? '🔑 Đã có mã PIN'
                      : '⚠️ Chưa có mã PIN'}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                className="h-11 flex-1 rounded-xl bg-emerald-700 font-semibold text-white"
                onClick={() => {
                  onEnterChildMode(learner)
                }}
              >
                Vào chế độ học
              </button>
              {onSetLearnerPin !== undefined && (
                <button
                  type="button"
                  className="h-11 rounded-xl border border-slate-300 px-3 dark:border-slate-700"
                  aria-label={`${pinConfigured.has(learner.id) ? 'Đổi' : 'Đặt'} mã PIN của ${learner.nickname}`}
                  onClick={() => {
                    onSetLearnerPin(learner)
                  }}
                >
                  {pinConfigured.has(learner.id) ? 'Đổi mã PIN' : 'Đặt mã PIN'}
                </button>
              )}
              <button
                type="button"
                className="h-11 rounded-xl border border-slate-300 px-4 dark:border-slate-700"
                aria-label={`Sửa hồ sơ ${learner.nickname}`}
                onClick={() => {
                  onEdit(learner)
                }}
              >
                Sửa
              </button>
            </div>
          </li>
        ))}
      </ul>

      <button
        type="button"
        className="h-12 rounded-xl border-2 border-dashed border-slate-300 font-semibold text-slate-700 md:max-w-sm dark:border-slate-700 dark:text-slate-300"
        onClick={onAdd}
      >
        + Thêm hồ sơ bé
      </button>

      {materialConsent !== null && <MaterialConsentCard api={materialConsent} />}

      <section
        aria-labelledby="family-learning"
        className="flex flex-col gap-2 rounded-2xl bg-sky-50 p-4 dark:bg-sky-950/40"
      >
        <h2 id="family-learning" className="text-lg font-semibold">
          Tình hình học tập
        </h2>
        <p className="text-slate-700 dark:text-slate-300">
          Tổng quan, nhận định có bằng chứng và việc nên làm tiếp.
        </p>
        <button
          type="button"
          className="h-11 self-start rounded-xl bg-sky-700 px-4 font-semibold text-white"
          onClick={onOpenLearning}
        >
          Xem tình hình học tập
        </button>
      </section>
    </div>
  )
}
